import { useState, useCallback, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { publicationApi } from '@/lib/client'
import type { Publication } from '../../../shared-lib/types'

interface UsePublicationsLoadMoreResult {
  data: Publication[]
  isLoading: boolean
  error: any
  hasMore: boolean
  loadMore: () => void
  isLoadingMore: boolean
}

interface UsePublicationsLoadMoreParams {
  type?: 'NEWS' | 'PAGE' | 'REPORT'
  categoryId?: string
  search?: string
  limit?: number
}

export function usePublicationsLoadMore(params: UsePublicationsLoadMoreParams = {}): UsePublicationsLoadMoreResult {
  const [currentPage, setCurrentPage] = useState(1)
  const [allPublications, setAllPublications] = useState<Publication[]>([])
  const [hasMore, setHasMore] = useState(true)
  
  const { type = 'NEWS', categoryId, search, limit = 12 } = params

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['publications-load-more', type, categoryId, search, currentPage],
    queryFn: async () => {
      const result = await publicationApi.getPublications({ 
        type,
        categoryId,
        search,
        status: 'PUBLISHED',
        page: currentPage.toString(),
        limit: limit.toString()
      })
      return result.data || []
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: hasMore
  })

  // Update allPublications when new data is fetched
  useEffect(() => {
    if (data && data.length > 0) {
      if (currentPage === 1) {
        setAllPublications(data)
      } else {
        setAllPublications(prev => [...prev, ...data])
      }
      
      // Check if we have more data
      if (data.length < limit) {
        setHasMore(false)
      }
    } else if (data && data.length === 0 && currentPage > 1) {
      setHasMore(false)
    }
  }, [data, currentPage, limit])

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore && !isFetching) {
      setCurrentPage(prev => prev + 1)
    }
  }, [isLoading, hasMore, isFetching])

  return {
    data: allPublications,
    isLoading: isLoading && currentPage === 1,
    error,
    hasMore,
    loadMore,
    isLoadingMore: isFetching && currentPage > 1
  }
}